import { FunctionComponent, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import {
  Container,
  Button,
  Row,
  Form,
  Breadcrumb,
  Spinner,
  Alert,
  Modal,
} from 'react-bootstrap'

import * as api from './api.client'
import hospitalsCss from './style.module.scss'
import { AdminUser } from './types'
import { ReactComponent as SaveIcon } from '../../assets/icons/save.svg'
import { AppMessage } from '../../utilities/messages'
import { hasBiUrl } from 'features/doctors/types'

const emptyUser: AdminUser = {
  id: 0,
  name: '',
  email: '',
  role: '',
  displayBi: false,
  urlBi: '',
}

const normalizeUser = (user: AdminUser): AdminUser => ({
  id: user.id,
  name: user.name || '',
  email: user.email || '',
  role: user.role || '',
  displayBi: Boolean(user.displayBi),
  urlBi: user.urlBi || '',
})

const EditUser: FunctionComponent = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [user, setUser] = useState<AdminUser>(emptyUser)
  const [loading, setLoading] = useState(true)
  const [showFormErrors, setShowFormErrors] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showBiPreview, setShowBiPreview] = useState(false)
  const [appMessage, setAppMessage] = useState<AppMessage>({ show: false })

  useEffect(() => {
    const loadUser = async () => {
      try {
        const found = await api.getUserById(Number.parseInt(id as string, 10))
        setUser(normalizeUser(found))
      } catch (error) {
        setAppMessage({
          show: true,
          success: false,
          message: `Erro ao carregar o usuário: ${(
            error as any
          ).messages?.join(', ') ||
            (error as any).message ||
            'tente novamente.'}`,
        })
      } finally {
        setLoading(false)
      }
    }

    loadUser()
  }, [id])

  const updateUser = async (event: React.ChangeEvent<HTMLFormElement>) => {
    event.preventDefault()
    const formIsValid = event.currentTarget.checkValidity()

    if (!formIsValid) {
      setShowFormErrors(true)
      return
    }

    setSaving(true)

    try {
      const updated = await api.updateUser(user.id, {
        name: user.name,
        email: user.email,
        displayBi: user.displayBi,
        urlBi: user.urlBi,
      })
      setUser(normalizeUser(updated))
      setAppMessage({
        show: true,
        success: true,
        message: 'Usuário atualizado com sucesso.',
      })
    } catch (error) {
      setAppMessage({
        show: true,
        success: false,
        message: `Erro ao atualizar o usuário: ${(error as any).messages?.join(
          ', '
        ) ||
          (error as any).message ||
          'tente novamente.'}`,
      })
    } finally {
      setSaving(false)
    }
  }

  const handleFormChange = ({
    target,
  }: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = target

    setUser({ ...user, [name]: type === 'checkbox' ? checked : value })
  }

  if (loading) {
    return (
      <Container fluid className={hospitalsCss.editFormContainer}>
        <Spinner animation="border" size="sm" />
      </Container>
    )
  }

  return (
    <Container fluid className={hospitalsCss.editFormContainer}>
      {appMessage.show && (
        <Alert
          variant={appMessage.success ? 'success' : 'danger'}
          style={{ marginTop: 20 }}
          onClose={() => setAppMessage({ show: false })}
          dismissible
        >
          {appMessage.message}
        </Alert>
      )}

      <Row>
        <Breadcrumb>
          <Breadcrumb.Item onClick={() => navigate('/users')}>
            Usuários
          </Breadcrumb.Item>
          <Breadcrumb.Item active>Editar usuário</Breadcrumb.Item>
        </Breadcrumb>
      </Row>

      <Form noValidate validated={showFormErrors} onSubmit={updateUser}>
        <Form.Group className="mb-3" controlId="formNome">
          <Form.Label>Nome</Form.Label>
          <Form.Control
            type="text"
            placeholder="Nome"
            name="name"
            value={user.name}
            onChange={handleFormChange}
            required
          />
          <Form.Control.Feedback type="invalid">
            Informe o nome do usuário.
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3" controlId="formEmail">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            placeholder="Email"
            name="email"
            value={user.email}
            onChange={handleFormChange}
            required
          />
          <Form.Control.Feedback type="invalid">
            Informe o email do usuário.
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3" controlId="formDisplayBi">
          <Form.Check
            type="switch"
            name="displayBi"
            label="Exibir Bi Médico"
            checked={user.displayBi}
            onChange={handleFormChange}
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="formUrlBi">
          <Form.Label>URL Bi Médico</Form.Label>
          <Form.Control
            type="text"
            placeholder="https://app.powerbi.com/..."
            name="urlBi"
            value={user.urlBi}
            onChange={handleFormChange}
          />
        </Form.Group>

        <div className={hospitalsCss.editFormActions}>
          <Button variant="primary" type="submit">
            {saving && (
              <div>
                <SaveIcon />{' '}
                <span className={hospitalsCss.txtButtonAddHospital}>Salvar</span>{' '}
                <Spinner animation="border" size="sm" />{' '}
              </div>
            )}
            {!saving && (
              <div>
                <SaveIcon />{' '}
                <span className={hospitalsCss.txtButtonAddHospital}>Salvar</span>
              </div>
            )}
          </Button>

          {hasBiUrl(user) && (
            <Button
              variant="outline-secondary"
              type="button"
              onClick={() => setShowBiPreview(true)}
            >
              Preview Bi Médico
            </Button>
          )}
        </div>
      </Form>

      <Modal
        show={showBiPreview}
        onHide={() => setShowBiPreview(false)}
        fullscreen
      >
        <Modal.Header closeButton>
          <Modal.Title>Preview Bi Médico</Modal.Title>
        </Modal.Header>
        <Modal.Body className={hospitalsCss.biPreviewModalBody}>
          <div className={hospitalsCss.biPreviewFrameWrap}>
            <iframe
              title="Preview Bi Médico"
              src={user.urlBi}
              className={hospitalsCss.biPreviewModalFrame}
              allowFullScreen
            />
            <div className={hospitalsCss.biBrandCover} aria-hidden="true" />
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  )
}

export default EditUser
